import { Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { DashboardBaseComponent } from '../shared/dashboard-base-component';
import { NgClass, SlicePipe } from '@angular/common';
import { ChartLoadingComponent } from '../chart-loading/chart-loading.component';
import {
  EtapaColumn,
  EtapaColumnBadge,
  EtapaColumnDot,
  EtapaColumnPill,
  EtapaColumnProgress,
  EtapaColumnText,
  EtapaColumnWithSubtext,
  ResumenEtapaConfig,
} from '../dashboard-table/dashboard-table.component';

export type DashboardTableSelectionMode = 'single' | 'multiple' | 'none';

@Component({
  selector: 'app-dashboard-table-selectable',
  templateUrl: './dashboard-table-selectable.component.html',
  styleUrls: ['./dashboard-table-selectable.component.scss'],
  imports: [SlicePipe, TableModule, ChartLoadingComponent, NgClass],
  standalone: true,
  providers: [
    {
      provide: DashboardBaseComponent,
      useExisting: forwardRef(() => DashboardTableSelectableComponent)
    }
  ]
})
export class DashboardTableSelectableComponent extends DashboardBaseComponent {
  private _loading?: boolean;

  /** Permite controlar externamente el estado de carga */
  @Input() set loading(val: boolean) {
    this._loading = val;
  }

  get isLoading(): boolean {
    if (this._loading !== undefined) {
      return this._loading;
    }
    return !this.config?.data;
  }

  isLazyLoading = false;

  /** Modo de selección: 'single' (radio/fila única), 'multiple' (checkboxes), o 'none' */
  @Input() selectionMode: DashboardTableSelectionMode = 'multiple';

  /** Indica si se debe mostrar la columna de checkbox/radio button de selección. Por defecto false */
  @Input() showSelectionColumn: boolean = false;

  @Input() styleClass?: string ;

  /** Permite selección con tecla meta/ctrl (por defecto true) */
  @Input() metaKeySelection: boolean = true;

  /** Campo clave para identificar los items (por defecto 'id') */
  @Input() dataKey: string = 'id';

  /** Elemento o arreglo de elementos seleccionados (soporta binding bidireccional [(selection)]) */
  @Input() selection: any = null;
  @Output() selectionChange = new EventEmitter<any>();

  /** Eventos adicionales de selección de PrimeNG */
  @Output() rowSelect = new EventEmitter<any>();
  @Output() rowUnselect = new EventEmitter<any>();
  @Output() headerCheckboxToggle = new EventEmitter<any>();

  /** Activa paginación server-side */
  @Input() lazy = false;

  /** Total de filas en el servidor (requerido cuando lazy = true) */
  @Input() totalRecords: number = 0;
  @Output() lazyLoad = new EventEmitter<TableLazyLoadEvent>();

  private _config!: ResumenEtapaConfig;

  @Input() set config(val: ResumenEtapaConfig) {
    this.isLazyLoading = false;
    this._config = val;
    if (val?.data) {
      this.isLoaded = true;
    }
  }

  get config() {
    return this._config;
  }

  get data(): any[] {
    return this.config?.data || [];
  }

  get columns() {
    return this._config?.columns || [];
  }

  onSelectionChange(value: any) {
    this.selection = value;
    this.selectionChange.emit(value);
  }

  onTableLazyLoad($event: TableLazyLoadEvent) {
    this.isLazyLoading = true;
    this.lazyLoad.emit($event);
  }

  barWidth(value: number, maxValue = 30): number {
    return Math.min((value / maxValue) * 100, 100);
  }

  asText(col: EtapaColumn): EtapaColumnText { return col as EtapaColumnText; }
  asDot(col: EtapaColumn): EtapaColumnDot { return col as EtapaColumnDot; }
  asProgress(col: EtapaColumn): EtapaColumnProgress { return col as EtapaColumnProgress; }
  asPill(col: EtapaColumn): EtapaColumnPill { return col as EtapaColumnPill; }
  asBadge(col: EtapaColumn): EtapaColumnBadge { return col as EtapaColumnBadge; }
  asWithSubtext(col: EtapaColumn): EtapaColumnWithSubtext { return col as EtapaColumnWithSubtext; }
}
